import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import CookieConsent from './components/CookieConsent';
import ChatWidget from './components/ChatWidget';
import BackButton from './components/BackButton';

// Critical pages (loaded immediately)
import LandingPage from './pages/LandingPageV2';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

// Lazy loaded pages (code split)
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const MentionsLegalesPage = lazy(() => import('./pages/MentionsLegalesPage'));
const TikTokLandingPage = lazy(() => import('./pages/TikTokLandingPage'));
const AttributionPage = lazy(() => import('./pages/AttributionPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelPage = lazy(() => import('./pages/PaymentCancelPage'));
const InstallAppPage = lazy(() => import('./pages/InstallAppPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const VsDocugovPage = lazy(() => import('./pages/VsDocugovPage'));
const VsDokkoaiAlternativePage = lazy(() => import('./pages/VsDokkoaiAlternativePage'));
const TemoignagesPage = lazy(() => import('./pages/TemoignagesPage'));
const DomainPage = lazy(() => import('./pages/DomainPage'));
const RecoursIndexPage = lazy(() => import('./pages/RecoursIndexPage'));
const RecoursPage = lazy(() => import('./pages/RecoursPage'));
const ReferralPage = lazy(() => import('./pages/ReferralPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogArticlePage = lazy(() => import('./pages/BlogPage').then(m => ({ default: m.default })));
const BlogIndexPage = lazy(() => import('./pages/BlogPage').then(m => ({ default: m.BlogIndexPage })));
const ModelesPage = lazy(() => import('./pages/ModelesPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const DemoPage = lazy(() => import('./pages/DemoPage'));
const ProductHuntPage = lazy(() => import('./pages/ProductHuntPage'));
const ProductHuntLaunchPage = lazy(() => import('./pages/ProductHuntLaunchPage'));
const AdLandingPage = lazy(() => import('./pages/AdLandingPage'));
const ScreenshotsPage = lazy(() => import('./pages/ScreenshotsPage'));
const SEOModelePage = lazy(() => import('./pages/SEOModelePage'));
const SEOIndexPage = lazy(() => import('./pages/SEOIndexPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ScannerPage = lazy(() => import('./pages/ScannerPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const DocumentDetailPage = lazy(() => import('./pages/DocumentDetailPageV2'));
const ActionsPage = lazy(() => import('./pages/ActionsPage'));
const RemindersPage = lazy(() => import('./pages/RemindersPage'));
const AssistantPage = lazy(() => import('./pages/AssistantPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const LetterDetailPage = lazy(() => import('./pages/LetterDetailPage'));
const LettersPage = lazy(() => import('./pages/LettersPage'));
const LetterGeneratorPage = lazy(() => import('./pages/LetterGeneratorPage'));
const EmailHistoryPage = lazy(() => import('./pages/EmailHistoryPage'));
const EmailDetailPage = lazy(() => import('./pages/EmailDetailPage'));
const AdminFeedbacksPage = lazy(() => import('./pages/AdminFeedbacksPage'));
const AdminSeoPage = lazy(() => import('./pages/AdminSeoPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const ABDashboardPage = lazy(() => import('./pages/ABDashboardPage'));
const SituationLandingPage = lazy(() => import('./pages/SituationLandingPage'));

import ChatBot from './components/ChatBot';

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background dark:bg-slate-950">
    <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <div className="App min-h-screen bg-background dark:bg-slate-950 text-foreground dark:text-slate-100 transition-colors duration-300">
              <Suspense fallback={<PageLoader />}>
              <Routes>
            {/* Routes publiques */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
            <Route path="/mentions" element={<MentionsLegalesPage />} />
            <Route path="/legal" element={<MentionsLegalesPage />} />
            <Route path="/tiktok" element={<TikTokLandingPage />} />
            <Route path="/admin/attribution" element={<AttributionPage />} />
            <Route path="/securite" element={<SecurityPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/installer" element={<InstallAppPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/vs-docugov" element={<VsDocugovPage />} />
            <Route path="/vs/docugov" element={<VsDocugovPage />} />
            <Route path="/vs-dokkoai-alternative" element={<VsDokkoaiAlternativePage />} />
            <Route path="/vs/dokkoai" element={<VsDokkoaiAlternativePage />} />
            <Route path="/dokkoai-alternative" element={<VsDokkoaiAlternativePage />} />
            <Route path="/temoignages" element={<TemoignagesPage />} />
            <Route path="/domaine/:slug" element={<DomainPage />} />
            <Route path="/recours" element={<RecoursIndexPage />} />
            <Route path="/recours/:slug" element={<RecoursPage />} />
            <Route path="/caf" element={<SituationLandingPage />} />
            <Route path="/amende" element={<SituationLandingPage />} />
            <Route path="/prefecture" element={<SituationLandingPage />} />
            {/* International SEO country pages */}
            <Route path="/dwp" element={<SituationLandingPage />} />
            <Route path="/home-office" element={<SituationLandingPage />} />
            <Route path="/hacienda" element={<SituationLandingPage />} />
            <Route path="/extranjeria" element={<SituationLandingPage />} />
            <Route path="/inps" element={<SituationLandingPage />} />
            <Route path="/questura" element={<SituationLandingPage />} />
            <Route path="/jobcenter" element={<SituationLandingPage />} />
            <Route path="/auslanderbehorde" element={<SituationLandingPage />} />
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/blog/:slug" element={<BlogArticlePage />} />
            <Route path="/modeles" element={<ModelesPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/go" element={<AdLandingPage />} />
            <Route path="/ads" element={<AdLandingPage />} />
            <Route path="/screenshots" element={<ScreenshotsPage />} />
            <Route path="/modeles" element={<SEOIndexPage />} />
            <Route path="/modele/:slug" element={<SEOModelePage />} />
            <Route path="/producthunt" element={<ProductHuntPage />} />
            <Route path="/ph" element={<ProductHuntPage />} />
            <Route path="/ph-launch" element={
              <ProtectedRoute>
                <ProductHuntLaunchPage />
              </ProtectedRoute>
            } />
            
            {/* Protected routes */}
            <Route path="/referral" element={
              <ProtectedRoute>
                <ReferralPage />
              </ProtectedRoute>
            } />

            {/* Routes de paiement */}
            <Route
              path="/pricing"
              element={<PricingPage />}
            />
            <Route
              path="/payment/success"
              element={
                <ProtectedRoute>
                  <PaymentSuccessPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment/cancel"
              element={
                <ProtectedRoute>
                  <PaymentCancelPage />
                </ProtectedRoute>
              }
            />

            {/* Routes protégées */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scanner"
              element={
                <ProtectedRoute>
                  <ScannerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documents"
              element={
                <ProtectedRoute>
                  <DocumentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documents/:id"
              element={
                <ProtectedRoute>
                  <DocumentDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/actions"
              element={
                <ProtectedRoute>
                  <ActionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reminders"
              element={
                <ProtectedRoute>
                  <RemindersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assistant"
              element={
                <ProtectedRoute>
                  <AssistantPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/letters"
              element={
                <ProtectedRoute>
                  <LettersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/letters/new"
              element={
                <ProtectedRoute>
                  <LetterGeneratorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/letters/:id"
              element={
                <ProtectedRoute>
                  <LetterDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-history"
              element={
                <ProtectedRoute>
                  <EmailHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-history/:id"
              element={
                <ProtectedRoute>
                  <EmailDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/feedbacks"
              element={
                <ProtectedRoute>
                  <AdminFeedbacksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/ab"
              element={
                <ProtectedRoute>
                  <ABDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/seo"
              element={
                <ProtectedRoute>
                  <AdminSeoPage />
                </ProtectedRoute>
              }
            />

            {/* Page 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          <CookieConsent />
          <BackButton />
          {/* ChatWidget disabled - was overlapping mobile nav */}
          {/* <ChatWidget /> */}
          <ChatBot />
          <Toaster position="top-right" expand={false} richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  </ThemeProvider>
  </HelmetProvider>
  );
}

export default App;
